import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext.js";
import { useTranslation } from "react-i18next";
import "./pricing.css";
import MarketingLayout from "./components/MarketingLayout.js";

// Load Stripe only when user runs checkout (dynamic import avoids loading Stripe on app init)
let stripePromise = null;
async function getStripePromise() {
  if (!stripePromise) {
    const { loadStripe } = await import("@stripe/stripe-js");
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key || key.includes("placeholder")) {
      throw new Error("Stripe is not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY.");
    }
    stripePromise = loadStripe(key);
  }
  return stripePromise;
}

export default function Pricing() {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Fetch price IDs from backend on component mount.
  // In dev (localhost), use relative /prices so CRA proxy forwards to backend and avoids CORS.
  useEffect(() => {
    const fetchPriceIds = async () => {
      try {
        const apiBase =
          typeof window !== "undefined" &&
          /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(window.location.origin)
            ? ""
            : (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
        const url = apiBase ? `${apiBase}/prices` : "/prices";
        const response = await fetch(url, { credentials: "include" });
        if (response.ok) {
          const data = await response.json();

          // Update plans with price IDs from backend
          setPlans(prevPlans => prevPlans.map(plan => {
            if (plan.name === "Pro") {
              return {
                ...plan,
                monthlyPriceId: data.pro?.monthly || process.env.REACT_APP_PRICE_PRO_MONTHLY || null,
                yearlyPriceId: data.pro?.yearly || process.env.REACT_APP_PRICE_PRO_YEARLY || null,
              };
            }
            if (plan.name === "Pro+") {
              return {
                ...plan,
                monthlyPriceId: data.proPlus?.monthly || process.env.REACT_APP_PRICE_ULT_MONTHLY || null,
                yearlyPriceId: data.proPlus?.yearly || process.env.REACT_APP_PRICE_ULT_YEARLY || null,
              };
            }
            if (plan.name === "Ultra") {
              return {
                ...plan,
                monthlyPriceId: data.ultra?.monthly || process.env.REACT_APP_PRICE_ULT_MONTHLY || null,
                yearlyPriceId: data.ultra?.yearly || process.env.REACT_APP_PRICE_ULT_YEARLY || null,
              };
            }
            return plan;
          }));
        }
      } catch (error) {
        console.error("Error fetching price IDs:", error);
      }
    };
    fetchPriceIds();
  }, []);

  // Plan price IDs - will be populated from backend
  const [plans, setPlans] = useState([
    {
      name: "Hobby",
      planId: "hobby",
      monthlyPrice: "Free",
      yearlyPrice: "Free",
      monthlyPriceNum: 0,
      yearlyPriceNum: 0,
      monthlyPriceId: null,
      yearlyPriceId: null,
      features: [
        "Zolaid AI chat with core models (fair-use limits)",
        "Zolaid Computer: built-in IDE, file tree & live site preview",
        "Starter website kit (sections, Stripe hooks, auth placeholders)",
        "Push AI replies into preview & generated files",
        "Standard support",
      ],
      button: "Get Started",
      popular: false,
    },
    {
      name: "Pro",
      planId: "pro",
      monthlyPrice: "$49",
      yearlyPrice: "$200",
      monthlyPriceNum: 49,
      yearlyPriceNum: 200,
      monthlyPriceId: null, // Will be set from backend
      yearlyPriceId: null, // Will be set from backend
      features: [
        "Higher AI usage for chat & builder workflows",
        "Full Zolaid Computer workspace (Code · Preview · optional VS Code tab)",
        "Website builder, export & deploy helpers",
        "Pro media tools: advanced image & video AI editors",
        "Structured AI contracts (render, plan, execute) in chat",
        "Email support",
      ],
      button: "Get Pro",
      popular: false,
    },
    {
      name: "Pro+",
      planId: "pro+",
      monthlyPrice: "$89",
      yearlyPrice: "$600",
      monthlyPriceNum: 89,
      yearlyPriceNum: 600,
      monthlyPriceId: null, // Will be set from backend
      yearlyPriceId: null, // Will be set from backend
      features: [
        "Elevated usage caps across chat, computer & editors",
        "Faster iteration on multi-file site projects",
        "Credits / tooling for heavier image & video jobs",
        "Priority handling for billing & account issues",
        "Early access to selected new product features",
        "API-friendly workflows where enabled",
      ],
      recommended: true,
      popular: true,
      button: "Get Pro+",
    },
    {
      name: "Ultra",
      planId: "ultra",
      monthlyPrice: "$229",
      yearlyPrice: "$2000",
      monthlyPriceNum: 229,
      yearlyPriceNum: 2000,
      monthlyPriceId: null, // Will be set from backend
      yearlyPriceId: null, // Will be set from backend
      features: [
        "Highest published usage tier for power users & studios",
        "Roadmap input & beta features",
        "Custom integration discussions (data, SSO, deployment)",
        "White-label and reseller options by agreement",
        "Dedicated success check-ins",
        "SLA options available",
      ],
      button: "Contact Sales",
      popular: false,
    },
  ]);

  const calculateSavings = (monthlyPrice, yearlyPrice) => {
    if (monthlyPrice === 0 || yearlyPrice === 0) return 0;
    const monthlyTotal = monthlyPrice * 12;
    const savings = monthlyTotal - yearlyPrice;
    return Math.round((savings / monthlyTotal) * 100);
  };

  const maxYearlySavingsPct = Math.max(
    0,
    ...plans
      .filter((p) => p.monthlyPriceNum > 0 && p.yearlyPriceNum > 0)
      .map((p) => calculateSavings(p.monthlyPriceNum, p.yearlyPriceNum))
  );

  const handleCheckout = async (plan) => {
    if (plan.name === "Hobby") {
      // Redirect to signup/login if not logged in
      if (!token) {
        navigate("/login");
        return;
      }
      // If logged in, they already have Hobby plan
      // Silently return - user is already on Hobby plan
      return;
    }
    
    if (plan.name === "Ultra") {
      // Contact sales
      window.location.href = "mailto:hello@zolaid.com?subject=Ultra Plan Inquiry";
      return;
    }

    // Check if user is logged in
    if (!token) {
      const proceed = window.confirm("You need to be logged in to subscribe. Would you like to login now?");
      if (proceed) {
        navigate("/login");
      }
      return;
    }

    setLoading(true);
    try {
      const priceId = billingCycle === "monthly" 
        ? plan.monthlyPriceId 
        : plan.yearlyPriceId;
      
      if (!priceId) {
        // Log error but don't show alert - button should be disabled
        console.warn(`Price ID not configured for ${plan.name} (${billingCycle}). Button should be disabled.`);
        setLoading(false);
        return;
      }

      // Get user ID from token
      let userId = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userId = payload.userId;
      } catch (e) {
        console.error("Error decoding token:", e);
      }

      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(apiUrl ? `${apiUrl}/create-checkout-session` : '/create-checkout-session', {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          priceId,
          userEmail: user?.email || null,
          userId: userId || null,
          successUrl: `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/pricing`,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create checkout session");
      }

      const data = await response.json();
      if (data.id) {
        // Redirect to Stripe Checkout
        const stripe = await getStripePromise();
        if (!stripe) {
          console.error("Stripe is not configured");
          return;
        }
        const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
        
        if (error) {
          console.error("Stripe redirect error:", error);
          // Silently handle error - no alert shown
        }
      } else {
        throw new Error("No session ID returned");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      alert(`Error processing payment: ${error.message || "Please try again or contact support."}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MarketingLayout>
    <div className="pricing-page">
      {/* Pricing Content */}
      <section className="pricing-section">
        <div className="pricing-header">
          <p className="pricing-eyebrow">Zolaid</p>
          <h1 className="pricing-title">{t("pricing_title") || "Plans for AI chat, sites & media"}</h1>
          <p className="pricing-subtitle">
            {t("pricing_subtitle") ||
              "Every tier includes the Zolaid assistant, Zolaid Computer (IDE + live preview), and the starter website kit. Upgrade for higher limits and pro image & video tools."}
          </p>
        </div>

        {/* Monthly / Yearly Toggle */}
        <div className="billing-toggle-wrapper">
          <div className="billing-toggle" role="tablist" aria-label="Billing cycle">
            <button
              type="button"
              role="tab"
              aria-selected={billingCycle === "monthly"}
              className={billingCycle === "monthly" ? "active" : ""}
              onClick={() => setBillingCycle("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billingCycle === "yearly"}
              className={billingCycle === "yearly" ? "active" : ""}
              onClick={() => setBillingCycle("yearly")}
            >
              Yearly
            </button>
            {billingCycle === "yearly" && maxYearlySavingsPct > 0 && (
              <span className="savings-badge">Save up to {maxYearlySavingsPct}% yearly</span>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="pricing-cards">
          {plans.map((plan) => {
            const savings = calculateSavings(plan.monthlyPriceNum, plan.yearlyPriceNum);
            const currentPrice = billingCycle === "monthly" 
              ? plan.monthlyPrice 
              : plan.yearlyPrice;
            const pricePerMonth = billingCycle === "yearly" && plan.yearlyPriceNum > 0
              ? `$${Math.round(plan.yearlyPriceNum / 12)}/mo`
              : null;

            return (
              <div
                key={plan.name}
                className={`pricing-card ${plan.recommended ? "recommended" : ""} ${plan.popular ? "popular" : ""}`}
              >
                {plan.recommended && (
                  <div className="recommended-badge">
                    <span className="badge-icon">✦</span>
                    <span>Best for creators</span>
                  </div>
                )}

                <div className="card-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="price-container">
                    <div className="price-main">
                      <span className="price-amount">{currentPrice}</span>
                      {billingCycle === "yearly" && plan.yearlyPriceNum > 0 && (
                        <span className="price-period">/year</span>
                      )}
                      {billingCycle === "monthly" && plan.monthlyPriceNum > 0 && (
                        <span className="price-period">/month</span>
                      )}
                    </div>
                    {pricePerMonth && (
                      <div className="price-per-month">{pricePerMonth} billed annually</div>
                    )}
                    {billingCycle === "yearly" && savings > 0 && (
                      <div className="savings-indicator">Save {savings}%</div>
                    )}
                  </div>
                </div>

                <ul className="features-list">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="feature-item">
                      <span className="check-icon">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  className={`pricing-button ${plan.recommended ? "recommended-btn" : ""}`}
                  onClick={() => handleCheckout(plan)}
                  disabled={loading || (plan.name !== "Hobby" && plan.name !== "Ultra" && !(billingCycle === "monthly" ? plan.monthlyPriceId : plan.yearlyPriceId))}
                  title={plan.name !== "Hobby" && plan.name !== "Ultra" && !(billingCycle === "monthly" ? plan.monthlyPriceId : plan.yearlyPriceId) ? "Payment configuration is being set up. Please check back soon." : ""}
                >
                  {loading ? "Processing..." : plan.button}
                </button>
              </div>
            );
          })}
        </div>

        {/* Product highlights — matches what ships in the app */}
        <div className="pricing-highlights">
          <h2 className="pricing-highlights-title">What you get in Zolaid</h2>
          <p className="pricing-highlights-lead">
            The same capabilities you see on the Features page and inside the product—no separate IDE subscription.
          </p>
          <div className="pricing-highlights-grid">
            <article className="pricing-highlight-card">
              <h3>Zolaid Computer</h3>
              <p>
                Virtual workspace with developer IDE, multi-file generated site, live preview, and optional embedded VS Code when you configure it.
              </p>
            </article>
            <article className="pricing-highlight-card">
              <h3>AI chat & contracts</h3>
              <p>
                Streaming assistant, render/execute/plan-style structured replies, and one-click push of HTML/CSS/JS into the computer preview.
              </p>
            </article>
            <article className="pricing-highlight-card">
              <h3>Website kit & commerce</h3>
              <p>
                Starter sections (product, pricing, auth, contact), Stripe checkout wiring to your backend, and export helpers for your project.
              </p>
            </article>
            <article className="pricing-highlight-card">
              <h3>Image & video studios</h3>
              <p>
                Pro-grade image and video AI editors in the app for marketing assets and creative cuts—scaled by plan limits.
              </p>
            </article>
          </div>
          <div className="pricing-highlights-cta">
            <Link to="/Features" className="pricing-link-btn">
              See feature tours
            </Link>
          </div>
        </div>

        {/* Teams & enterprise */}
        <div className="business-plans">
          <h2 className="business-title">Organizations</h2>
          <p className="business-subtitle">
            Need multiple seats, invoicing, or a custom deployment? We’ll align the plan with how you use Zolaid Computer and chat.
          </p>

          <div className="business-cards">
            <div className="business-card">
              <h3>Teams</h3>
              <div className="biz-price">Custom <span className="price-meta">per seat</span></div>
              <p className="biz-lead">For agencies and product teams building with Zolaid daily.</p>
              <ul>
                <li>Central billing and seat management</li>
                <li>Guided onboarding for Zolaid Computer + site kit</li>
                <li>Shared best practices for prompts and exports</li>
                <li>Usage reporting (where available)</li>
              </ul>
              <button type="button" className="pricing-button" onClick={() => navigate('/signup')}>
                Start team signup
              </button>
            </div>

            <div className="business-card">
              <h3>Enterprise</h3>
              <div className="biz-price">Custom</div>
              <p className="biz-lead">Security reviews, SSO, and deployment options.</p>
              <ul>
                <li>Invoice / PO billing</li>
                <li>SSO and directory integration (roadmap—ask us)</li>
                <li>Private cloud or VPC discussions</li>
                <li>DPA, custom terms, and priority support</li>
              </ul>
              <button
                type="button"
                className="pricing-button"
                onClick={() => (window.location.href = "mailto:hello@zolaid.com?subject=Enterprise%20Plan%20Inquiry")}
              >
                Contact sales
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="pricing-faq">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Can I change plans later?</h3>
              <p>Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
            </div>
            <div className="faq-item">
              <h3>What payment methods do you accept?</h3>
              <p>Paid plans are billed securely with Stripe (major cards and other methods Stripe supports in your region).</p>
            </div>
            <div className="faq-item">
              <h3>Is there a free tier?</h3>
              <p>
                Hobby stays free with fair-use limits. When we run a Pro trial, we’ll show it in the app—check your account banner after signup.
              </p>
            </div>
            <div className="faq-item">
              <h3>Do you offer refunds?</h3>
              <p>We offer a 30-day money-back guarantee on all paid plans. No questions asked.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </MarketingLayout>
  );
}
